import { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Cake, Loader2, Sparkles, Eye, EyeOff, AlertTriangle, ShieldCheck, Users, Share2 } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { trackPageView, trackEvent, trackError } from "@/lib/track";
import { API, axios } from "@/api";
import { toast } from "sonner";

export default function OnboardingPage() {
  // iter314 — single-screen signup. Previously a 4-step wizard
  // (Name+Birthday+Gender → Favorite Color → Email+Password+City →
  // Confirm Birthday) that duplicated work UserOnboarding does later.
  // Now: signup is a single "Create Account" form; preferences are
  // collected exclusively in UserOnboarding (/onboarding/user).
  const [searchParams] = useSearchParams();
  // iter314 — initial formData derives referral_code from URL once.
  // No useEffect mirror needed (the previous one tripped
  // react-hooks/set-state-in-effect and provided no real value —
  // users with a new ?ref= would already have a fresh page mount).
  const [formData, setFormData] = useState(() => ({
    first_name: "", last_initial: "", email: "", password: "",
    birthday: "", gender: "prefer_not_to_say",
    favorite_color: "#0ea5e9", city: "Sacramento", state: "CA",
    referral_code: (searchParams.get("ref") || "").toUpperCase(),
  }));
  const [error, setError] = useState("");
  // iter314 — derive initial mode from URL ``?ref=`` param so the
  // signup screen shows up immediately for invited users; eliminates
  // the previous useEffect-based ``setIsLogin(false)`` that ran on
  // mount and tripped the react-hooks/set-state-in-effect lint rule.
  const [isLogin, setIsLogin] = useState(() => !searchParams.get("ref"));
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [birthdayConfirmed, setBirthdayConfirmed] = useState(false);
  const [refFromUrl, setRefFromUrl] = useState(() => Boolean(searchParams.get("ref")));
  const [showReferral, setShowReferral] = useState(() => Boolean(searchParams.get("ref")));
  const [pendingDeletion, setPendingDeletion] = useState(null);
  const [coppaRejection, setCoppaRejection] = useState(null);
  const [reactivating, setReactivating] = useState(false);
  const { user, token, loading: authLoading, login, register } = useAuth();
  const navigate = useNavigate();
  const justRegistered = useRef(false);

  useEffect(() => { if (!authLoading && token && user && !justRegistered.current) navigate("/", { replace: true }); }, [authLoading, token, user, navigate]);

  if (authLoading) return <div className="min-h-screen flex items-center justify-center bg-slate-50"><Loader2 className="w-8 h-8 animate-spin text-sky-500" /></div>;

  const handleNameChange = (field, value) => {
    if (field === "first_name") setFormData({ ...formData, first_name: value.charAt(0).toUpperCase() + value.slice(1) });
    else if (field === "last_initial") setFormData({ ...formData, last_initial: value.toUpperCase().charAt(0) });
  };

  const handleSubmit = async () => {
    setError(""); setLoading(true);
    try {
      if (isLogin) {
        const result = await login(formData.email, formData.password);
        // Check if user needs to choose onboarding path
        const me = await axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } });
        const userData = me.data;
        // iter315 — admins bypass the onboarding gate entirely. They
        // are seeded without onboarding fields by design (see iter300
        // notes in /app/memory/test_credentials.md) and otherwise get
        // trapped on /fork → /onboarding/user, which blocks QA of any
        // protected page including /profile.
        if (userData.is_admin) {
          navigate("/");
        } else if (!userData.onboarding_path) {
          navigate("/fork");
        } else if (!userData.onboarding_complete) {
          navigate(userData.onboarding_path === "vendor" ? "/onboarding/vendor" : "/onboarding/user");
        } else {
          navigate(userData.onboarding_path === "vendor" ? "/vendor/dashboard" : "/");
        }
      }
      else {
        justRegistered.current = true;
        await register(formData);
        trackEvent("signup_complete");
        navigate("/fork"); // New users always go to fork
      }
    }
    catch (e) {
      const detail = e.response?.data?.detail;
      const status = e.response?.status;
      const respData = e.response?.data;

      // v5.3.1: COPPA rejection screen
      if (status === 403 && respData?.status === "blocked_coppa") {
        setCoppaRejection({
          message: respData.message,
          daysUntilEligible: respData.days_until_eligible,
          parentMessage: respData.parent_message,
        });
        // Clear form data (COPPA: no data retained)
        setFormData({ first_name: "", last_initial: "", email: "", password: "", birthday: "", gender: "", favorite_color: "#0ea5e9", city: "Sacramento", state: "CA", referral_code: "" });
        return;
      }

      // Humanize Pydantic validation arrays
      if (Array.isArray(detail)) {
        const fieldMap = {
          first_name: "Please enter your first name",
          last_initial: "Please enter your last initial",
          email: "Please enter your email address",
          password: "Please enter a password",
          birthday: "Please enter your birthday",
        };
        const msgs = detail.map(err => {
          const field = err.loc?.[err.loc.length - 1];
          if (err.type === "value_error" && field === "email") return "Please enter a valid email address";
          return fieldMap[field] || null;
        }).filter(Boolean);
        setError(msgs.length > 0 ? msgs[0] : "Please check your information and try again");
      } else {
        // Check for pending_deletion response
        const detailStr = typeof detail === "string" ? detail : "";
        if (detailStr.startsWith("pending_deletion|")) {
          const delDate = detailStr.split("|")[1];
          setPendingDeletion({ date: delDate, email: formData.email, password: formData.password });
        } else {
          setError(typeof detail === "string" ? detail : "Something went wrong");
        }
      }
      trackError(isLogin ? "login" : "signup", e);
    }
    finally { setLoading(false); }
  };

  const handleTestUser = async () => {
    setError(""); setLoading(true);
    try {
      await login("founder@akenterpriseinc.com", "password123");
      // Admin/test account — check onboarding state
      const me = await axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } });
      if (!me.data.onboarding_path) navigate("/fork");
      else navigate("/");
    }
    catch (e) { setError("Test login failed."); trackError("test_login", e); }
    finally { setLoading(false); }
  };

  const handleTestVendor = async () => {
    setError(""); setLoading(true);
    try {
      await login("vendor_test@test.com", "password123");
      const me = await axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } });
      if (!me.data.onboarding_path) {
        navigate("/fork");
      } else if (!me.data.onboarding_complete) {
        navigate("/onboarding/vendor");
      } else {
        localStorage.setItem("vendorModeSelected", "true");
        localStorage.setItem("vendorModeActive", "true");
        navigate("/vendor/dashboard");
      }
    }
    catch (e) { setError("Vendor test login failed."); trackError("test_vendor_login", e); }
    finally { setLoading(false); }
  };

  // Steps: 1 = Name/Birthday/Gender, 2 = Favorite Color, 3 = Email/Password/Location, 4 = Confirm Birthday
  const calculateAge = (dateStr) => {
    if (!dateStr) return null;
    const bday = new Date(dateStr + "T00:00:00");
    const today = new Date();
    let age = today.getFullYear() - bday.getFullYear();
    if (today.getMonth() < bday.getMonth() || (today.getMonth() === bday.getMonth() && today.getDate() < bday.getDate())) age--;
    return age;
  };
  const userAge = calculateAge(formData.birthday);
  const isUnder13 = userAge !== null && userAge < 13;
  // iter314 — single-screen signup gate: every required field must be
  // filled and the birthday checkbox checked. COPPA-blocked accounts
  // can't submit.
  const canCreateAccount = Boolean(
    formData.first_name &&
    formData.last_initial &&
    formData.email &&
    formData.password &&
    formData.birthday &&
    !isUnder13 &&
    birthdayConfirmed
  );

  const formatBirthdayDisplay = (dateStr) => {
    if (!dateStr) return "";
    const [y, m, d] = dateStr.split("-");
    const months = ["January","February","March","April","May","June","July","August","September","October","November","December"];
    return `${months[parseInt(m, 10) - 1]} ${parseInt(d, 10)}, ${y}`;
  };

  // ── v5.3.1: COPPA Rejection Screen ──
  if (coppaRejection) {
    const handleShareWithParent = () => {
      const shareText = "Check out BirthdayDeaLight — an app that finds birthday deals at restaurants and experiences near you. I found it but I am a little too young to sign up. You should try it! Download at birthdaydealight.com";
      if (navigator.share) {
        navigator.share({ title: "BirthdayDeaLight", text: shareText }).catch(() => {});
      } else {
        navigator.clipboard?.writeText(shareText);
        toast.success("Message copied to clipboard!");
      }
    };

    return (
      <div className="min-h-screen bg-white flex flex-col items-center justify-center p-6" data-testid="coppa-rejection-screen">
        <div className="w-full max-w-sm text-center">
          {/* Friendly cake icon */}
          <div className="w-16 h-16 mx-auto mb-5 rounded-full bg-amber-100 flex items-center justify-center">
            <Cake className="w-8 h-8 text-amber-500" />
          </div>

          {/* Section 1: Plain language */}
          <h2 className="text-xl font-bold text-slate-800 mb-3" data-testid="coppa-heading">You need to be a little older</h2>
          <p className="text-sm text-slate-600 leading-relaxed">
            BirthdayDeaLight is for people 13 and up. It is a rule that helps keep younger kids safe online. We cannot let you sign up just yet but we hope to see you soon.
          </p>
          {coppaRejection.daysUntilEligible > 0 && (
            <div className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-amber-50 border border-amber-200">
              <Sparkles className="w-4 h-4 text-amber-500" />
              <span className="text-sm font-semibold text-amber-700" data-testid="coppa-days">
                Your 13th birthday is in {coppaRejection.daysUntilEligible} days
              </span>
            </div>
          )}

          {/* Section 2: Parent invitation */}
          <div className="mt-8 rounded-2xl border-2 border-amber-300 bg-amber-50/50 p-5 text-left">
            <h3 className="text-sm font-bold text-amber-800 mb-2">Tell a parent or guardian about us</h3>
            <p className="text-xs text-amber-700 leading-relaxed">
              Ask them to check out BirthdayDeaLight. Parents love finding birthday deals too and they can sign up right now.
            </p>
            <button onClick={handleShareWithParent}
              className="mt-4 w-full py-3 rounded-xl bg-amber-500 text-white text-sm font-bold flex items-center justify-center gap-2 active:scale-[0.98] transition-transform"
              data-testid="share-with-parent-btn">
              <Share2 className="w-4 h-4" /> Share with a Parent
            </button>
          </div>

          {/* Section 3: Return reminder */}
          <p className="mt-8 text-xs text-slate-400" data-testid="coppa-return-note">
            Come back on your 13th birthday and you can sign up.
          </p>
        </div>
      </div>
    );
  }

  // ── Pending Deletion Reactivation Screen ──
  if (pendingDeletion) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 via-white to-slate-50 flex flex-col items-center justify-center p-6" data-testid="reactivation-screen">
        <div className="w-full max-w-sm bg-white rounded-2xl shadow-xl p-6 text-center">
          <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-red-100 flex items-center justify-center">
            <AlertTriangle className="w-7 h-7 text-red-500" />
          </div>
          <h2 className="text-lg font-bold text-slate-800 mb-2">Your account is scheduled for deletion</h2>
          <p className="text-xs text-slate-500 mb-6">
            Your account will be permanently deleted on <span className="font-semibold text-red-600">{pendingDeletion.date}</span>. Changed your mind?
          </p>
          <div className="space-y-2">
            <button
              onClick={async () => {
                setReactivating(true);
                try {
                  const res = await axios.post(`${API}/user/account/reactivate`, {
                    email: pendingDeletion.email,
                    password: pendingDeletion.password,
                  });
                  if (res.data.access_token) {
                    localStorage.setItem("token", res.data.access_token);
                    toast.success("Account reactivated!");
                    window.location.href = "/";
                  }
                } catch (e) {
                  toast.error(e.response?.data?.detail || "Failed to reactivate");
                } finally { setReactivating(false); }
              }}
              disabled={reactivating}
              className="w-full px-4 py-3 text-sm font-semibold text-white bg-sky-500 rounded-xl hover:bg-sky-600 transition disabled:opacity-50"
              data-testid="cancel-deletion-btn"
            >
              {reactivating ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : "Cancel Deletion"}
            </button>
            <button
              onClick={() => { setPendingDeletion(null); setError(""); }}
              className="w-full px-4 py-3 text-xs font-medium text-slate-400 hover:text-slate-600 transition"
              data-testid="proceed-deletion-btn"
            >
              I understand, proceed with deletion
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-sky-50 via-white to-orange-50 flex flex-col">
      <div className="flex-1 flex flex-col items-center justify-center p-6">
        <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="w-full max-w-md">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-br from-sky-400 to-sky-500 shadow-xl shadow-sky-500/30 mb-4"><Cake className="w-10 h-10 text-white" /></div>
            <h1 className="text-3xl font-bold text-slate-800">BirthdayDeaLight</h1>
            <p className="text-slate-500 mt-2">Claim your free birthday treats</p>
          </div>
          <div className="bg-white rounded-3xl shadow-xl p-6 space-y-5">
            {isLogin ? (
              <>
                <div><label className="text-sm font-medium text-slate-600 block mb-2">Email</label><input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-sky-500 focus:ring-2 focus:ring-sky-500/20 outline-none transition" placeholder="you@example.com" data-testid="login-email" /></div>
                <div><label className="text-sm font-medium text-slate-600 block mb-2">Password</label>
                  <div className="relative"><input type={showPassword ? "text" : "password"} value={formData.password} onChange={(e) => setFormData({ ...formData, password: e.target.value })} className="w-full px-4 py-3 pr-12 rounded-xl border border-slate-200 focus:border-sky-500 focus:ring-2 focus:ring-sky-500/20 outline-none transition" placeholder="Enter password" data-testid="login-password" />
                    <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors" data-testid="toggle-password-visibility">{showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}</button>
                  </div>
                  <div className="text-right mt-1.5"><button type="button" onClick={() => navigate("/forgot-password")} className="text-xs text-sky-500 font-medium hover:text-sky-600 transition-colors" data-testid="forgot-password-link">Forgot Password?</button></div>
                </div>
              </>
            ) : (
              /* iter314 — single-screen signup. Was a 4-step wizard;
                 onboarding-style preference collection now lives
                 exclusively in /onboarding/user (the actual onboarding).
                 Only the fields legally / functionally required to
                 create an account live here: name (display), email,
                 password, birthday (COPPA gate). */
              <div className="space-y-5" data-testid="signup-form">
                <div className="grid grid-cols-2 gap-4">
                  <div><label className="text-sm font-medium text-slate-600 block mb-2">First Name</label><input type="text" value={formData.first_name} onChange={(e) => handleNameChange("first_name", e.target.value)} className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-sky-500 focus:ring-2 focus:ring-sky-500/20 outline-none transition" placeholder="Alex" data-testid="register-first-name" /></div>
                  <div><label className="text-sm font-medium text-slate-600 block mb-2">Last Initial</label><input type="text" maxLength={1} value={formData.last_initial} onChange={(e) => handleNameChange("last_initial", e.target.value)} className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-sky-500 focus:ring-2 focus:ring-sky-500/20 outline-none transition text-center uppercase" placeholder="M" data-testid="register-last-initial" /></div>
                </div>
                <div><label className="text-sm font-medium text-slate-600 block mb-2">Email</label><input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-sky-500 focus:ring-2 focus:ring-sky-500/20 outline-none transition" placeholder="you@example.com" data-testid="register-email" /></div>
                <div><label className="text-sm font-medium text-slate-600 block mb-2">Password</label>
                  <div className="relative"><input type={showPassword ? "text" : "password"} value={formData.password} onChange={(e) => setFormData({ ...formData, password: e.target.value })} className="w-full px-4 py-3 pr-12 rounded-xl border border-slate-200 focus:border-sky-500 focus:ring-2 focus:ring-sky-500/20 outline-none transition" placeholder="6+ characters" data-testid="register-password" />
                    <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors" data-testid="toggle-register-password-visibility">{showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}</button>
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-600 block mb-2">Birthday</label>
                  <input type="date" value={formData.birthday} onChange={(e) => { setFormData({ ...formData, birthday: e.target.value }); setBirthdayConfirmed(false); }} className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-sky-500 focus:ring-2 focus:ring-sky-500/20 outline-none transition" data-testid="register-birthday" />
                  {isUnder13 && (
                    <div className="mt-3 bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3" data-testid="under-13-warning">
                      <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm font-semibold text-amber-800">You must be 13 or older to use BirthdayDeaLight.</p>
                        <p className="text-xs text-amber-600 mt-1">We take your safety seriously. You cannot proceed with registration.</p>
                      </div>
                    </div>
                  )}
                  {formData.birthday && !isUnder13 && (
                    <label className="flex items-start gap-2 mt-3 cursor-pointer select-none" data-testid="birthday-confirm-label">
                      <input
                        type="checkbox"
                        checked={birthdayConfirmed}
                        onChange={(e) => setBirthdayConfirmed(e.target.checked)}
                        className="mt-0.5 w-4 h-4 rounded border-slate-300 text-sky-500 focus:ring-sky-500 accent-sky-500 shrink-0"
                        data-testid="birthday-confirm-checkbox"
                      />
                      <span className="text-xs text-slate-600">I confirm my birthday is <strong>{formatBirthdayDisplay(formData.birthday)}</strong>. This cannot be changed later.</span>
                    </label>
                  )}
                </div>
                {/* Collapsed referral code — most users won't expand */}
                {!showReferral ? (
                  <button type="button" onClick={() => setShowReferral(true)} className="text-xs text-sky-500 font-medium hover:text-sky-600 transition-colors flex items-center gap-1" data-testid="show-referral-toggle">
                    <Users className="w-3.5 h-3.5" /> Have a referral code?
                  </button>
                ) : (
                  <div>
                    <label className="text-xs font-medium text-slate-400 block mb-1.5">Referral code <span className="text-slate-300">(optional)</span></label>
                    <div className="relative">
                      <Users className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-300" />
                      <input type="text" value={formData.referral_code} onChange={(e) => { setFormData({ ...formData, referral_code: e.target.value.toUpperCase() }); if (refFromUrl && e.target.value !== searchParams.get("ref")?.toUpperCase()) setRefFromUrl(false); }} className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-slate-200 focus:border-sky-500 focus:ring-2 focus:ring-sky-500/20 outline-none transition text-sm uppercase tracking-wider font-mono" placeholder="Enter code" data-testid="register-referral-code" />
                    </div>
                    {refFromUrl && formData.referral_code && (
                      <p className="text-[11px] text-emerald-600 font-medium mt-1.5 flex items-center gap-1" data-testid="referral-applied-msg">
                        <Sparkles className="w-3 h-3" /> Referral code applied — you were invited by a friend
                      </p>
                    )}
                  </div>
                )}
              </div>
            )}

            {error && <p className="text-red-500 text-sm text-center">{error}</p>}

            {/* iter314 — single submit button replaces the per-step navigation. */}
            {isLogin ? (
              <button onClick={handleSubmit} disabled={loading} className="w-full py-4 rounded-full bg-gradient-to-r from-sky-400 to-sky-500 text-white font-semibold shadow-lg shadow-sky-500/30 hover:shadow-sky-500/50 transition-all btn-press disabled:opacity-50" data-testid="submit-btn">{loading ? <Loader2 className="w-5 h-5 animate-spin mx-auto" /> : "Sign In"}</button>
            ) : (
              <button
                onClick={handleSubmit}
                disabled={loading || !canCreateAccount}
                className="w-full py-4 rounded-full bg-gradient-to-r from-sky-400 to-sky-500 text-white font-semibold shadow-lg shadow-sky-500/30 hover:shadow-sky-500/50 transition-all btn-press disabled:opacity-50 flex items-center justify-center gap-2"
                data-testid="submit-btn"
              >
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <><ShieldCheck className="w-5 h-5" />Create Account</>}
              </button>
            )}

            <p className="text-center text-sm text-slate-500">{isLogin ? "Don't have an account?" : "Already have an account?"} <button onClick={() => { setIsLogin(!isLogin); setError(""); }} className="text-sky-500 font-medium" data-testid="toggle-auth-mode">{isLogin ? "Sign Up" : "Sign In"}</button></p>
            {!localStorage.getItem("hide_quick_test") && (
              <div className="mt-4 pt-4 border-t border-dashed border-slate-200 space-y-2">
                <button onClick={handleTestUser} disabled={loading} className="w-full py-3 rounded-xl bg-sky-50 text-sky-700 font-medium text-sm border border-sky-200 hover:bg-sky-100 transition-all flex items-center justify-center gap-2" data-testid="test-user-btn">{loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}Enter as User</button>
                <button onClick={handleTestVendor} disabled={loading} className="w-full py-3 rounded-xl bg-amber-50 text-amber-700 font-medium text-sm border border-amber-200 hover:bg-amber-100 transition-all flex items-center justify-center gap-2" data-testid="test-vendor-btn">{loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}Enter as Vendor</button>
              </div>
            )}
          </div>
        </motion.div>
      </div>
    </div>
  );
}
